<template>
  <ModuleContainer />
</template>

<script>
import L from "@/plugins/leaflet";
import ModuleMixin from "@/components/Map/ModuleMixin";
import { createNavigateUrl } from "@/utils/Navigation";
import { ButtonGroupControl } from "@/components/Map/leaflet/ButtonGroupControl";
import { ButtonControl } from "../Map/leaflet/ButtonControl";

function isPointInBounds(lat, lng, b) {
  return lat >= b.south && lat <= b.north && lng >= b.west && lng <= b.east;
}

export default {
  mixins: [ModuleMixin],

  props: {
    utilities: {
      type: Array,
      default: undefined
    },

    currentUtility: {
      type: Object,
      default: undefined
    },

    visibleOverlayBounds: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      // Map
      utilitiesGroup: null,
      utilitiesMapping: {},
      pointVisible: true,
      markerList: [],
      loading: false
    };
  },

  computed: {
    filteredByBounds() {
      if (!this.utilities) return [];
      if (this.visibleOverlayBounds.length === 0) return this.utilities;
      return this.utilities.filter(u =>
        this.visibleOverlayBounds.some(b =>
          isPointInBounds(Number(u.latitude), Number(u.longitude), b)
        )
      );
    }
  },

  watch: {
    filteredByBounds(utilities) {
      this.displayUtilities(utilities);
    },

    currentUtility(utility) {
      if (utility) {
        this.highlightPoint(utility);
      }
    },

    loading(val) {
      this.controls[0].buttons[0].setLoading(val);
    }
  },

  methods: {
    onInit() {
      // Use this.map to access leaflet map
      this.utilitiesGroup = L.markerClusterGroup({
        chunkedLoading: true,
        chunkProgress: this.updateProgressBar.bind(this)
      }).addTo(this.map);

      const button = new ButtonControl(
        "Utility Point Visible",
        "place",
        "",
        () => {
          this.pointVisible = !this.pointVisible;

          this.displayUtilities(this.filteredByBounds);
        }
      );

      const buttonGroup = new ButtonGroupControl([button]);

      this.addControl(buttonGroup);
    },

    onDestroy() {
      if (this.utilitiesGroup) this.utilitiesGroup.remove();
    },

    initMarketList(utilities) {
      for (let i = 0, len = utilities.length; i < len; i++) {
        const point = utilities[i];
        const icon = point?.category?.icon || "/img/default-avatar.png";
        const categoryName = point?.category?.name || "Unknown";
        const marker = L.marker([point.latitude, point.longitude], {
          icon: L.divIcon({
            className: "custom-div-icon",
            html: `<div class='marker-pin'/><img src="${icon}" />`,
            iconSize: [30, 42],
            iconAnchor: [15, 42]
          })
        });

        const navigationUrl = createNavigateUrl({
          destination: {
            latitude: Number(point.latitude),
            longitude: Number(point.longitude)
          },
          params: [
            {
              key: "travelmode",
              value: "driving" // may be "walking", "bicycling" or "transit" as well
            },
            {
              key: "dir_action",
              value: "navigate" // this instantly initializes navigation using the given travel mode
            }
          ]
        });
        const geojson = point.geojson ? JSON.parse(point.geojson) : {};
        marker.bindPopup(`
          <div style="text-align: center; overflow: hidden">
            
            ${geojson.image
            ? `<a href="${geojson.image}" target="_blank">
                    <img src="${geojson.image}" style="height: 240px">
                  </a>`
            : ``
          }
          </div>
          ${point.UID ? `<div style="font-size: 14px; color: #666; margin: 4px 0;">UID: ${point.UID}</div>` : ''}
        <div style="font-size: 16px; font-weight: bold;">${point.feature_name
          }</div>
        
        <div style="display: flex; align-item: center">
          <div style="flex: 1; min-width: 0">
            <div style="display: flex; align-items: center">
              <span class="material-icons" style="width: 18px; height: 18px; font-size: 18px; margin-right: 5px">home</span>
              ${point.location}
            </div>
            <div style="display: flex; align-items: center">
              <img src="${icon
          }" style="width: 18px; height: 18px; margin-right: 5px" />
              ${categoryName}
            </div>
          </div>

          <a href="${navigationUrl}" target="_blank" style="cursor: pointer; flex: none; margin-left: 10px">
            <i class="material-icons">directions</i>
          </a>
        </div>
      `);

        // this.utilitiesGroup.addLayer(marker);

        this.utilitiesMapping[point.id] = marker;
        this.markerList[this.markerList.length] = marker;
      }
    },

    highlightPoint(point) {
      const marker = this.utilitiesMapping[point.id];

      if (!marker) return;

      this.map.flyTo(marker.getLatLng(), 20);

      this.map.once("moveend", () => {
        this.$nextTick(() => {
          marker.openPopup();
        });
      });
    },

    clearPoints() {
      this.utilitiesMapping = {};
      this.utilitiesGroup.clearLayers();
    },

    displayUtilities(utilities) {
      this.markerList = [];
      // This method called after API call, so this.map can be null because of user go to other route
      if (!this.map) return;
      this.clearPoints();

      if (!utilities || utilities.length === 0) return;

      if (!this.pointVisible) return;
      this.initMarketList(utilities);

      this.utilitiesGroup.addLayers(this.markerList);
      // this.map.addLayer(this.utilitiesGroup);

      // const bounds = this.utilitiesGroup.getBounds();
      // if (bounds.isValid()) {
      //   this.map.fitBounds(bounds);
      // }
    },

    // eslint-disable-next-line no-unused-vars
    updateProgressBar(processed, total, elapsed) {
      this.loading = processed !== total;
    }
  }
};
</script>

<style>
.marker-pin {
  width: 30px;
  height: 30px;
  border-radius: 50% 50% 50% 0;
  background: #c30b82;
  position: absolute;
  transform: rotate(-45deg);
  left: 50%;
  top: 50%;
  margin: -15px 0 0 -15px;
}

.marker-pin::after {
  content: "";
  width: 24px;
  height: 24px;
  margin: 3px 0 0 3px;
  background: #fff;
  position: absolute;
  border-radius: 50%;
}

.custom-div-icon img {
  position: absolute;
  width: 18px;
  height: 16px;
  font-size: 22px;
  left: 0;
  right: 0;
  margin-top: 8px;
  margin-left: 6px;
  text-align: center;
  transform: rotate(45deg);
  z-index: 100;
}
</style>

<style scoped>
.gis-map-container {
  position: absolute;
  top: 0.625em;
  left: 1.25em;
  z-index: 1000;
  width: 26em;
}

@media (max-width: 600px) {
  .gis-map-container {
    top: 0.625em;
    left: 0.625em;
    width: 18.75em;
  }
}

.top-categories-container {
  display: flex;
  justify-content: space-around;
}

.more-categories-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
}

.category-item {
  width: auto;
  margin: 0.625em;
}

.clear-current-category-button {
  position: realtive;
  top: 0;
  right: 0;
  z-index: 5000;
  margin: 0;
}

.utilities-viewer-container d-flex align-center {
  position: relative;
}

/* Point Item */
.point-item-container {
  display: flex;
  align-items: center;
  border-bottom: 0.0625em solid rgba(0, 0, 0, 0.1);
}

.point-content-container {
  flex: 1;
  min-width: 0;
  padding: 0.625em 0.9375em;
}

.point-name-text {
  font-size: 1em;
  font-weight: bold;
  color: var(--text-color);
}

.point-address-text {
  color: var(--secondary-text);
}

.utilities-container {
  max-height: 45vh;
  min-height: 6.25em;
  overflow-x: hidden;
  overflow-y: auto;
}

.add-marker-button {
  flex: none;
  margin: 0 0.625em;
}

.footer-container {
  text-align: right;
}

.show-more-less-button {
  color: var(--text-color);
  text-align: center;
  vertical-align: middle;
  font-size: 1.5em;
  line-height: 2.625em;
}
</style>
